import React, { FC, RefObject } from 'react';
import classNames from 'classnames';

import { ClosePlayerButton } from '../../skin/close-player-button/component';
import { BaseControlName } from '../../../types/controls';
import { useSkinStage } from '../../../hooks/skin/use-skin-stage';
import { SubtitlesProvider } from '../../subtitles/subtitles-provider/components';
import { ContextMenuContainer } from '../../skin/context-menu/container';
import { UserActionIndicatorView } from '../../skin/user-action-indicator/user-action-indicator-view/component';
import { VodKeyboardController } from '../../../../vod-player/ui/VodKeyboardController';
import { UserActionIndicatorProvider } from '../../skin/user-action-indicator/user-action-indicator-provider/component';
import { VodLayout } from '../../../../vod-player/ui/VodLayout';
import { VodPlayButton } from '../../../../vod-player/ui/VodPlayButton';
import { GlobalKeyboardHandlerTogglersProvider } from '../../../contexts/global-keyboard-handler-togglers';
import EpisodePlayerControlPanel from '../episode-control-panel/component';
import { SubtitlesPortalRoot } from '../../subtitles/subtitles-portal-root/component';
import { useEpisodePrerollDurationWatcher } from '../../../../episode-player/hooks/use-episode-preroll-duration-watcher';
import { EpisodePrerollViewContainer } from '../episode-preroll-view/container';
import { EpisodeAgeRestriction } from '../../../../episode-player/ui/episode-player/EpisodeAgeRestriction';
import { EpisodeSkipControlsContainer } from '../../../../episode-player/ui/episode-player/EpisodeSkipControlsContainer';
import { EpisodePlayerMetaControl } from '../../../../episode-player/ui/episode-player/EpisodePlayerMetaContainer';

import { useOnEpisodeEnd } from './hooks/use-on-episode-end';
import styles from './styles.module.css';

interface Props {
    contentId: string;
    filmId: string;
    seasonId: string;
    psid: string;
    onClose?: () => void;
    onSelectEpisode?: () => void;
    className?: string;
    rootContainerRef: RefObject<HTMLDivElement> | null;
}

export const EpisodePlayerSkin: FC<Props> = ({
    contentId,
    filmId,
    seasonId,
    psid,
    onClose,
    onSelectEpisode,
    className,
    rootContainerRef,
}) => {
    useEpisodePrerollDurationWatcher(contentId);

    const { checkIsStageCurrent, switchToPrerollStage, switchToDefaultStage } = useSkinStage();

    useOnEpisodeEnd(contentId, onClose);

    return (
        <SubtitlesProvider>
            <GlobalKeyboardHandlerTogglersProvider>
                <UserActionIndicatorProvider>
                    <VodKeyboardController
                        isMovieActionsBlocked={checkIsStageCurrent('graceCommunication')}
                        onClose={onClose}
                    />
                    <VodLayout
                        className={classNames(styles.layout, className)}
                        renderTopControls={
                            checkIsStageCurrent('default', 'graceCommunication', 'preroll')
                                ? () => (
                                      <ClosePlayerButton
                                          name={BaseControlName.CLOSE}
                                          onClick={onClose}
                                          className={styles.closeButton}
                                          contentId={contentId}
                                      />
                                  )
                                : undefined
                        }
                        renderBottomControls={
                            checkIsStageCurrent('default') || checkIsStageCurrent('preroll')
                                ? ({ setIsHidingControlsBlocked }) => (
                                      <div className={styles.controlPanelContainer}>
                                          <EpisodePlayerControlPanel
                                              contentId={contentId}
                                              filmId={filmId}
                                              seasonId={seasonId}
                                              setIsHidingControlsBlocked={
                                                  setIsHidingControlsBlocked
                                              }
                                              psid={psid}
                                              onSelectEpisode={onSelectEpisode}
                                          />
                                      </div>
                                  )
                                : undefined
                        }
                        renderControls={({ isControlsVisible }) => (
                            <>
                                <UserActionIndicatorView className={styles.indicator} />
                                {checkIsStageCurrent('default') && (
                                    <>
                                        <SubtitlesPortalRoot
                                            isControlsVisible={isControlsVisible}
                                        />
                                        <VodPlayButton className={styles.playButton} />
                                        {!isControlsVisible && (
                                            <EpisodeAgeRestriction contentId={contentId} />
                                        )}
                                        <EpisodeSkipControlsContainer
                                            contentId={contentId}
                                            className={styles.skipButtonGroup}
                                        />
                                    </>
                                )}
                                {!checkIsStageCurrent('preroll') && (
                                    <EpisodePlayerMetaControl
                                        contentId={contentId}
                                        className={styles.meta}
                                        isControlsVisible={isControlsVisible}
                                    />
                                )}
                                <EpisodePrerollViewContainer
                                    contentId={contentId}
                                    filmId={filmId}
                                    onStart={switchToPrerollStage}
                                    onClose={switchToDefaultStage}
                                    checkStage={checkIsStageCurrent}
                                    className={styles.prerollView}
                                />
                            </>
                        )}
                        renderPopups={() =>
                            rootContainerRef && (
                                <ContextMenuContainer rootContainerRef={rootContainerRef} />
                            )
                        }
                    />
                </UserActionIndicatorProvider>
            </GlobalKeyboardHandlerTogglersProvider>
        </SubtitlesProvider>
    );
};
